import { ReactElement, useMemo, useCallback, useEffect } from 'react';
import { useFormik, FormikHelpers } from 'formik';
import { Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { SelectWithScopeOption } from '~globals/types/commons';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { hasError } from '~utils/formHelpers';
import Subscription from '~components/Subscription';
import SelectWithScopeField from '~components/SelectWithScopeField';

import {
  ReportsDownloadSectionWrapperFormProps,
  ReportsDownloadSectionWrapperFormValues,
} from './types';
import {
  FIELDS_NAME,
  initialValues,
  getValidationSchema,
  getMinDatePicker,
} from './utils';

const ReportsDownloadSectionWrapperForm = ({
  onSubmit: onSubmitProps,
  loadingSubmit,
  errorSubmit,
  withSelectorType = false,
  gutters = true,
}: ReportsDownloadSectionWrapperFormProps): ReactElement => {
  const { isCarrier } = useTypePartnerCompany();

  const selectorTypeOptions = useMemo<SelectWithScopeOption[]>(() => {
    const baseOptions: SelectWithScopeOption[] = [
      {
        value: 'items',
        label: 'Visitas',
        scopeDescription: 'Historial de visitas en el período',
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.ReportAdvanced,
        },
      },
      {
        value: 'itemTasks',
        label: 'Artículos / tareas',
        scopeDescription: 'Detalle de artículos y tareas en el período',
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmItemTask,
        },
      },
    ];

    if (isCarrier) {
      return [
        {
          value: 'routes',
          label: 'Rutas',
          scopeDescription: 'Historial de rutas en el período',
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.ReportAdvanced,
          },
        },
        ...baseOptions,
      ];
    }

    return baseOptions;
  }, [isCarrier]);

  const onSubmit = useCallback(
    async (
      values: ReportsDownloadSectionWrapperFormValues,
      { setSubmitting }: FormikHelpers<ReportsDownloadSectionWrapperFormValues>,
    ) => {
      await onSubmitProps(values);

      setSubmitting(false);
    },
    [onSubmitProps],
  );

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    getFieldProps,
    handleSubmit,
    isSubmitting,
    dirty,
    isValid,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: getValidationSchema(withSelectorType),
    onSubmit,
  });

  useEffect(() => {
    if (!loadingSubmit && !errorSubmit) {
      resetForm();
    }
  }, [loadingSubmit, errorSubmit, resetForm]);

  const handleChangeDate = useCallback(
    (fieldName: string) =>
      (value: Date | null): void => {
        setFieldValue(fieldName, value);
      },
    [setFieldValue],
  );

  const handleBlurDate = useCallback(
    (fieldName: string) => (): void => {
      setFieldTouched(fieldName, true);
    },
    [setFieldTouched],
  );

  const minDateFrom = useMemo(() => {
    if (isCarrier) return undefined;

    return getMinDatePicker();
  }, [isCarrier]);

  const minDateTo = useMemo(
    () => values[FIELDS_NAME.DATE_FROM] ?? minDateFrom,
    [values, minDateFrom],
  );

  return (
    <Stack
      spacing={2}
      paddingX={gutters ? 4 : 0}
      component="form"
      onSubmit={handleSubmit}
    >
      {withSelectorType && (
        <SelectWithScopeField
          label="Tipo de reporte"
          options={selectorTypeOptions}
          error={hasError(touched, errors, FIELDS_NAME.TYPE)}
          fullWidth
          autoComplete="off"
          {...getFieldProps(FIELDS_NAME.TYPE)}
          value={values[FIELDS_NAME.TYPE] ?? ''}
        />
      )}

      <Stack spacing={2} direction="row">
        <Subscription.DataRetentionMin.DatePicker
          label="Desde"
          value={values[FIELDS_NAME.DATE_FROM]}
          onChange={handleChangeDate(FIELDS_NAME.DATE_FROM)}
          disableFuture
          minDate={minDateFrom}
          renderInput={(props) => (
            <TextField
              {...props}
              autoComplete="off"
              fullWidth
              onBlur={handleBlurDate(FIELDS_NAME.DATE_FROM)}
              error={hasError(touched, errors, FIELDS_NAME.DATE_FROM)}
            />
          )}
        />

        <Subscription.DataRetentionMin.DatePicker
          label="Hasta"
          value={values[FIELDS_NAME.DATE_TO]}
          onChange={handleChangeDate(FIELDS_NAME.DATE_TO)}
          disableFuture
          minDate={minDateTo}
          renderInput={(props) => (
            <TextField
              {...props}
              autoComplete="off"
              fullWidth
              onBlur={handleBlurDate(FIELDS_NAME.DATE_TO)}
              error={hasError(touched, errors, FIELDS_NAME.DATE_TO)}
            />
          )}
        />
      </Stack>

      <LoadingButton
        type="submit"
        variant="contained"
        color="primary"
        loading={loadingSubmit || isSubmitting}
        disabled={!dirty || !isValid}
        sx={{ minWidth: 210, alignSelf: 'center' }}
      >
        Generar
      </LoadingButton>
    </Stack>
  );
};

export default ReportsDownloadSectionWrapperForm;
